<script setup lang="ts">
  /**
   * Register toolbar — 03-components/12f-register-toolbar · OSCloud DS 1.1.125
   *
   * The row directly above a register: one control that narrows what is listed (`lead`) and the
   * register's own actions at the trailing edge (`actions`). It belongs to the register BELOW it,
   * which is what its single bottom gap says. Layout only — no surface, border, type or rhythm
   * beyond that gap; whatever sits in it keeps its own metrics and states.
   *
   * No props and no modifiers; every slot is optional by omission.
   * - `lead`: exactly ONE control — a BfSearchField, a BfSelect filter or a chip group.
   * - `count`: one BfCounter, on the control's own line.
   * - `actions`: at most three buttons, one primary, primary LAST in the DOM, default size.
   *
   * Not BfPageHeader's `actions` (beside the page title) and not a filter strip inside the table
   * box. One create action per page: if it is here, the page header does not also carry it.
   */
</script>

<template>
  <div class="register-toolbar">
    <div v-if="$slots.lead || $slots.count" class="register-toolbar__lead">
      <slot name="lead" />
      <span v-if="$slots.count" class="register-toolbar__count"><slot name="count" /></span>
    </div>
    <div v-if="$slots.actions" class="register-toolbar__actions">
      <slot name="actions" />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .register-toolbar {
    /* Widths, not tokens: the measure of the leading control, and the width below which the row
       reflows. A page whose control needs more room raises the measure on the block. */
    --register-toolbar-lead-measure: 420px;
    --register-toolbar-lead-min: 240px;

    /* flex-end, never center: the lead may carry a label, so the row aligns to the CONTROL. */
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: var(--bf-sp-3);
    margin-bottom: var(--bf-sp-3);

    /* The row reflows (actions onto their own line, still trailing) long before it stacks. */
    &__lead {
      display: flex;
      align-items: flex-end;
      gap: var(--bf-sp-2);
      flex: 1 1 var(--register-toolbar-lead-min);
      min-width: 0;
      max-width: var(--register-toolbar-lead-measure);
    }

    /* Whatever sits in the slot fills it; the slot sets no metrics of its own. Slotted content
       carries the page's scope id, not this block's, hence :slotted. */
    &__lead > :slotted(.form-field),
    &__lead > :slotted(.select-field) {
      flex: 1 1 auto;
      min-width: 0;
    }

    &__count {
      flex: 0 0 auto;
      margin-bottom: 12px; /* half of the 44px control, centred on its middle */
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: var(--bf-sp-2);
      flex: 0 0 auto;
      flex-wrap: wrap;
    }
  }

  /* --bf-bp-sm: one column. __actions is column-reverse, so the primary lands on top while DOM
     and focus order stay primary-last. The row's flex-basis is a WIDTH — in a column it would
     become a 240px height, so it is reset. */
  @media (max-width: 600px) {
    .register-toolbar {
      align-items: stretch;
      flex-direction: column;
    }

    .register-toolbar__lead {
      flex: 0 0 auto;
      max-width: none;
    }

    .register-toolbar__actions {
      flex-direction: column-reverse;
      align-items: stretch;
    }

    .register-toolbar__actions > :slotted(.button) {
      width: 100%;
    }
  }
</style>
